import { ChangeDetectionStrategy, Component, ViewChild, inject, input } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormArray, FormGroup } from '@angular/forms';
import { Doctor, PatientDoctor } from '@xmedical/core';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { DoctorAddCardComponent } from '../doctor-add-card/doctor-add-card.component';
import { DoctorDisplayCardComponent } from '../doctor-display-card/doctor-display-card.component';
import { DoctorEditCardComponent } from '../doctor-edit-card/doctor-edit-card.component';
import { DoctorFormShape, makeDoctorFormGroup } from '../patient-form.service';

export type DoctorKind = 'family' | 'referring';

// `Doctor` minus the `id` field — what the form stores.
function toPatientDoctor(doctor: Doctor): PatientDoctor {
  const { id, ...rest } = doctor;
  return rest;
}

// New-doctor payload returned by DoctorAddCardComponent (no id yet — it's
// assigned by the persist callback when the container creates the record).
type NewDoctorValue = Omit<Doctor, 'id'>;

@Component({
  selector: 'xcs-patient-doctors-fieldset',
  templateUrl: './patient-doctors-fieldset.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    XmedicalUiModule,
    DoctorDisplayCardComponent,
  ],
})
export class PatientDoctorsFieldsetComponent {
  private matDialog = inject(MatDialog);

  readonly array = input.required<FormArray<FormGroup<DoctorFormShape>>>();
  readonly kind = input.required<DoctorKind>();
  readonly heading = input.required<string>();
  readonly emptyMessage = input.required<string>();

  // Search callback supplied by the form (originally from the container's
  // DoctorRepositoryService). Lower-casing happens here so the fieldset's
  // template binding stays simple.
  readonly doctorSearch = input.required<(query: string) => Observable<Doctor[]>>();

  // Persist callbacks the container wires to repository writes. The fieldset
  // opens the dialog cards itself, takes the form value back via dialogRef
  // close, then calls the relevant callback. This keeps the dumb cards
  // free of repository injections.
  readonly doctorAddPersist = input.required<(value: NewDoctorValue) => Observable<Doctor>>();
  readonly doctorEditPersist = input.required<(id: number, value: Doctor) => Observable<Doctor>>();

  @ViewChild('autocomplete') private autocomplete?: AppMatAutocompleteComponent;

  readonly searchDoctors = (query: string): Observable<Doctor[]> => {
    return this.doctorSearch()(query.toLowerCase());
  };

  doctorSelected(doctor: Doctor): void {
    this.array().push(makeDoctorFormGroup(toPatientDoctor(doctor)));
  }

  remove(index: number): void {
    this.array().removeAt(index);
  }

  addNewDoctor(): void {
    const ref = this.matDialog.open(DoctorAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
    });
    ref.afterClosed().pipe(
      filter((value): value is NewDoctorValue => !!value),
      switchMap(value => this.doctorAddPersist()(value)),
    ).subscribe(saved => {
      // Reuse the autocomplete's existing flow — searching on the new name
      // surfaces the freshly-persisted doctor for the user to select, which
      // pushes a form group via `doctorSelected()`.
      this.autocomplete?.search(saved.name);
    });
  }

  edit(index: number): void {
    const existing = this.array().at(index).value as PatientDoctor;
    const ref = this.matDialog.open(DoctorEditCardComponent, {
      data: existing,
      panelClass: 'mat-confirmation-dialog-responsive',
    });
    ref.afterClosed().pipe(
      filter((value): value is Doctor | PatientDoctor => !!value),
      switchMap(value => {
        // Patch the form first so the UI reflects the change immediately.
        this.array().at(index).patchValue(value);
        // Persist only if this is a real (id-bearing) record.
        if ('id' in value && (value as Doctor).id) {
          return this.doctorEditPersist()((value as Doctor).id, value as Doctor);
        }
        return of(null);
      }),
    ).subscribe();
  }
}
